import { faCreditCard as farCreditCard } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import { faCalendarDays as fasCalendarDays, faPlay as fasPlay } from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type {
	AbstractSearchFormProps,
	AbstractSearchFormState,
} from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { AbstractSearchForm } from "@component/assets/AbstractListingCalculator/AbstractSearchForm";
import { Config } from "@config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import bankmonitorLogo from "@img/logos/bankmonitor.svg";
import { formatText } from "@ui/format";
import autobind from "autobind-decorator";
import { FormCurrency, FormNumber, ProgressButton } from "bm-ui-kit";
import type { FormApi } from "final-form";
import type { ReactNode } from "react";
import { Card, Form } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";
import {
	INCOME_DATA_LIST,
	LOAN_AMOUNT_DATA_LIST,
	MAX_EMPLOYMENT_MONTHS,
	MAX_LOAN_AMOUNT,
	MAX_MATURITY,
	MAX_TOTAL_INCOME,
	MIN_LOAN_AMOUNT,
	MIN_MATURITY,
} from "../Calculator/data";
import type { PersonalLoanCalculatorVariant, PersonalLoanSearchParams } from "../Calculator/types";
import type { LoanGoal } from "../LeadForm/types";
import "./style.scss";

interface Props extends AbstractSearchFormProps<PersonalLoanSearchParams> {
	loanGoal?: LoanGoal;
	hideLoanGoal?: boolean;
	variant?: PersonalLoanCalculatorVariant;
}

interface State extends AbstractSearchFormState {
	customFieldsCollapse: boolean;
}

export class PersonalLoanSearchForm extends AbstractSearchForm<PersonalLoanSearchParams, Props, State> {
	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			customFieldsCollapse: false,
		};
	}

	protected renderFields(_values: PersonalLoanSearchParams): ReactNode {
		return (
			<>
				<FormCurrency
					label="Hitelösszeg"
					name="loanAmount"
					prepend={<FontAwesomeIcon icon={farCreditCard} />}
					validate={this.validateLoanAmount}
					dataList={LOAN_AMOUNT_DATA_LIST}
					placeholder="Hitelösszeg"
					required
				/>
				<FormNumber
					label="Futamidő"
					name="maturityYears"
					prepend={<FontAwesomeIcon icon={fasCalendarDays} />}
					placeholder="Futamidő"
					infoTooltip="Mennyi ideig szeretnéd törleszteni a hitelt?"
					append="év"
					validate={this.validateMaturityYears}
					min={MIN_MATURITY}
					max={MAX_MATURITY}
					step={1}
					required
				/>
				<FormCurrency
					label="Havi nettó jövedelem"
					name="income"
					infoTooltip="Mekkora jövedelem érkezik havonta a bankszámládra?"
					validate={this.validateIncome}
					dataList={INCOME_DATA_LIST}
					placeholder="Összeg"
					required
				/>
			</>
		);
	}

	public render(): ReactNode {
		return (
			<FinalForm<PersonalLoanSearchParams> {...this.getFinalFormProps()}>
				{({ handleSubmit, form, values }) => {
					const { ...searchParams } = values;
					this.initializeFormHandling({ handleSubmit, form, values });

					return (
						<div className="small-calculator">
							<div className="small-calculator-container fixed-container-height">
								<div className="small-calculator-title d-none d-md-block">
									Személyi kölcsön kalkulátor
								</div>
								<div className="small-calculator-step-container">
									<Form
										className="small-calculator-step card fixed-height"
										onSubmit={(element) => {
											const { hasValidationErrors, errors } = form.getState();

											if (hasValidationErrors) {
												this.sendEvent("BM_calculationInvalid", {
													...errors,
												});
											}

											return handleSubmit(element);
										}}
										noValidate
									>
										<Card.Header className="text-center text-white bg-primary py-3 card-header-border">
											<strong className="text-white">Mennyi pénzre van szükséged?</strong>
										</Card.Header>
										<Card.Body>
											{this.renderFields(searchParams as unknown as PersonalLoanSearchParams)}
										</Card.Body>
										<Card.Footer>
											{this.renderButtons(
												searchParams as unknown as PersonalLoanSearchParams,
												form as unknown as FormApi<PersonalLoanSearchParams>,
											)}
										</Card.Footer>
									</Form>
								</div>
								<div className="d-none d-md-flex align-items-center justify-content-between pr-3 pt-1">
									<a href="/" className="ml-auto" target="_blank" rel="noreferrer">
										<img
											decoding="async"
											width="176"
											height="32"
											src={bankmonitorLogo}
											alt="Bankmonitor"
											loading="eager"
										/>
									</a>
								</div>
							</div>
						</div>
					);
				}}
			</FinalForm>
		);
	}

	protected renderCalculateButton(_valid: boolean): ReactNode {
		const { buttonText = "Kalkuláció indítása" } = this.props;
		const { calculateButtonState } = this.state;

		return (
			<ProgressButton
				type="submit"
				state={calculateButtonState}
				icon={<FontAwesomeIcon icon={fasPlay} />}
				errorChildren="Hiba"
				doneChildren="Kész"
				loadingChildren="Kalkulálás..."
			>
				{buttonText}
			</ProgressButton>
		);
	}

	@autobind
	private validateLoanAmount(loanAmount: number): string {
		if (isNaN(loanAmount)) {
			return "A hitelösszeg megadása kötelező!";
		}

		if (loanAmount < MIN_LOAN_AMOUNT) {
			return `A minimálisan felvehető hitelösszeg ${formatText(MIN_LOAN_AMOUNT, "ft")}!`;
		}

		if (loanAmount > MAX_LOAN_AMOUNT) {
			return `A maximálisan felvehető hitelösszeg ${formatText(MAX_LOAN_AMOUNT, "ft")}!`;
		}
	}

	@autobind
	private validateMaturityYears(maturityYears: number, values: PersonalLoanSearchParams): string {
		if (isNaN(maturityYears)) {
			return "A futamidő megadása kötelező!";
		}

		if (maturityYears < MIN_MATURITY) {
			return `A minimálisan megadható futamidő ${MIN_MATURITY} év!`;
		}

		if (maturityYears > MAX_MATURITY) {
			return `A maximálisan megadható futamidő ${MAX_MATURITY} év!`;
		}

		if (
			[
				"SZABAD_FELHASZNALAS",
				"GEPJARMU_VASARLAS",
				"SAJAT_BANKI_HITELKIVALTAS",
				"IDEGEN_BANKI_HITELKIVALTAS",
				"ENERGETIKAI_KORSZERUSITES",
			].includes(values.loanGoal) &&
			maturityYears > 8
		) {
			return "Csökkentsd a futamidőt, vagy válassz másik hitelcélt.";
		}
	}

	@autobind
	private validateEmploymentMonths(employmentMonths: number): string {
		if (isNaN(employmentMonths)) {
			return "A munkaviszony hosszának megadása kötelező!";
		}

		if (employmentMonths < 0) {
			return "A munkaviszony hossza nem lehet negatív szám!";
		}

		if (employmentMonths > MAX_EMPLOYMENT_MONTHS) {
			return `A megadható maximális munkaviszony ${MAX_EMPLOYMENT_MONTHS} hónap!`;
		}
	}

	@autobind
	private validateIncome(income: number): string {
		if (isNaN(income)) {
			return "A jövedelem megadása kötelező!";
		}

		if (income < Config.getMinWage()) {
			return `A megadható minimális összjövedelem ${formatText(
				Config.getMinWage(),
				"ft",
			)}, amely megegyezik az aktuális minimálbérrel.`;
		}

		if (income > MAX_TOTAL_INCOME) {
			return `A megadható maximális jövedelem ${formatText(MAX_TOTAL_INCOME, "ft")}!`;
		}
	}

	private onCollapseClick(): void {
		const { customFieldsCollapse } = this.state;

		this.setState({
			customFieldsCollapse: !customFieldsCollapse,
		});

		this.sendEvent("BM_advancedCalculation", { action: customFieldsCollapse ? "close" : "open" });
	}

	private hasExtra(): boolean {
		return Config.getCurrentUser().hasRole("PERSONAL_LOAN");
	}

	protected getComponentName(): string {
		return "PersonalLoanSearchForm";
	}
}
